import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  IconButton,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  InputLabel as MuiInputLabel
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  VolunteerActivism as OfferingIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { donationService } from '../../services/donationService';
import { parentService } from '../../services/api';
import { uploadService } from '../../services/uploadService';
import dayjs from 'dayjs';
import DatePickerField from '../../components/DatePickerField';

const statuses = [
  { value: 'pending', label: 'Pendiente', color: 'warning' },
  { value: 'confirmed', label: 'Confirmado', color: 'success' },
  { value: 'cancelled', label: 'Cancelado', color: 'error' }
];

const currencies = [
  { value: 'RUB', label: 'Rublo Ruso', symbol: '₽' },
  { value: 'USD', label: 'Dólar Americano', symbol: '$' },
  { value: 'EUR', label: 'Euro', symbol: '€' }
];

const paymentMethods = [
    { value: 'bank_transfer', label: 'Transferencia Bancaria' },
    { value: 'cash', label: 'Efectivo' },    
    { value: 'check', label: 'Cheque' },
    { value: 'other', label: 'Otro' }
];

const schema = yup.object({
  donor_name: yup.string().nullable(),
  russian_name: yup.string().nullable(),
  parent_id: yup.number().nullable(),
  amount: yup.number()
    .required('El monto es requerido')
    .min(0.01, 'El monto debe ser mayor a 0')
    .typeError('Debe ser un número válido'),
  currency: yup.string().required('La moneda es requerida'),
  donation_date: yup.string()
    .required('La fecha es requerida')
    .test('not-future', 'La fecha no puede ser futura', (value) => {
      return dayjs(value).isSameOrBefore(dayjs(), 'day');
    }),
  donation_type: yup.string().nullable(),
  reference_number: yup.string().nullable(),
  status: yup.string().required('El estado es requerido'),
  notes: yup.string().nullable(),
  receipt_url: yup.string().nullable()
});

const OfferingForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const [parents, setParents] = useState<any[]>([]);
  const [searchParentOpen, setSearchParentOpen] = useState(false);
  const [parentSearch, setParentSearch] = useState('');
  
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedParent, setSelectedParent] = useState<any>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      donor_name: '',
      russian_name: '',
      parent_id: null,
      amount: 0,
      currency: 'RUB',
      donation_date: dayjs().format('YYYY-MM-DD'),
      donation_type: 'bank_transfer',
      reference_number: '',
      status: 'confirmed',
      notes: '',
      receipt_url: ''
    }
  });

  useEffect(() => {
    loadParents();
    if (isEdit) {
      loadDonation();
    }
  }, [id]);

  const loadParents = async () => {
    try {
      const response = await parentService.getAll({ per_page: 500, active: true });
      setParents(response.data.data || []);
    } catch (err) {
      console.error('Error loading parents:', err);
    }
  };

  const loadDonation = async () => {
    try {
      setLoading(true);
      const response = await donationService.getById(parseInt(id!));
      const data = response.data;
      reset(data);
      if (data.parent) setSelectedParent(data.parent);
      if (data.receipt_url) {
        setUploadedFile({
          name: 'Comprobante existente',
          url: data.receipt_url,
          type: 'application/pdf'
        });
        setValue('receipt_url', data.receipt_url);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar donación');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        setError('Solo se permiten imágenes (JPEG, PNG, GIF) y archivos PDF');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('El archivo no puede ser mayor a 5MB');
        return;
      }
      setPaymentFile(file);
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => setFilePreview(e.target?.result as string);
        reader.readAsDataURL(file);
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleFileUpload = async (file: File): Promise<string> => {
    try {
      setUploadingFile(true);
      const response = await uploadService.uploadPaymentProof(file);
      setUploadedFile({
        name: file.name,
        url: response.data.file_url,
        type: file.type
      });
      setValue('receipt_url', response.data.file_url);
      return response.data.file_url;
    } catch (err: any) {
      throw new Error('Error al subir archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setPaymentFile(null);
    setFilePreview(null);
    setValue('receipt_url', '');
  };

  const handleSelectParent = (parent: any) => {
    setSelectedParent(parent);
    setValue('parent_id', parent.id);
    setValue('donor_name', parent.spanish_name || '');
    setValue('russian_name', parent.russian_name || '');
    setSearchParentOpen(false);
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);
      setError(null);

      let receiptUrl = data.receipt_url;
      if (paymentFile && !uploadedFile) {
        receiptUrl = await handleFileUpload(paymentFile);
      }

      const donationData = {
        ...data,
        receipt_url: receiptUrl
      };

      if (isEdit) {
        await donationService.update(parseInt(id!), donationData);
      } else {
        await donationService.create(donationData);
      }

      setSuccess(true);
      setTimeout(() => navigate('/donations'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar donación');
    } finally {
      setLoading(false);
    }
  };

  const filteredParents = parents.filter(p =>
    (p.spanish_name?.toLowerCase().includes(parentSearch.toLowerCase()) ||
     p.russian_name?.toLowerCase().includes(parentSearch.toLowerCase()))
  );

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton component={Link} to="/donations" sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {isEdit ? 'Editar Donación' : 'Nueva Donación / Ofrenda'}
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ¡Donación guardada exitosamente! Redirigiendo...
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Información del Donante */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información del Donante
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={() => setSearchParentOpen(true)}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      Buscar padre existente
                    </Button>
                    
                    {selectedParent && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Padre seleccionado: {selectedParent.spanish_name || selectedParent.russian_name}
                        <Button size="small" onClick={() => {
                          setSelectedParent(null);
                          setValue('parent_id', null);
                          setValue('donor_name', '');
                          setValue('russian_name', '');
                        }} sx={{ ml: 2 }}>
                          Cambiar
                        </Button>
                      </Alert>
                    )}
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="donor_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Nombre en Español"
                          disabled={!!selectedParent}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="russian_name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Nombre en Ruso"
                          disabled={!!selectedParent}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Datos de la Donación */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Datos de la Donación
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="amount"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          label="Monto *"
                          error={!!errors.amount}
                          helperText={errors.amount?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon />
                              </InputAdornment>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="currency"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.currency}>
                          <InputLabel>Moneda *</InputLabel>
                          <Select {...field} label="Moneda *">
                            {currencies.map((c) => (
                              <MenuItem key={c.value} value={c.value}>
                                {c.symbol} - {c.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.currency && (
                            <FormHelperText>{errors.currency.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Controller
                      name="donation_date"
                      control={control}
                      render={({ field }) => (
                        <DatePickerField
                          {...field}
                          label="Fecha de Ingreso"
                          error={!!errors.donation_date}
                          helperText={errors.donation_date?.message}
                          disabled={loading}
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="donation_type"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                            <InputLabel>Método de Pago</InputLabel>
                            <Select {...field} label="Método de Pago">
                                {paymentMethods.map((method) => (
                                    <MenuItem key={method.value} value={method.value}>
                                    {method.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="reference_number"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Número de Referencia"
                          placeholder="Transferencia, cheque, etc."
                        />
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.status}>
                          <InputLabel>Estado *</InputLabel>
                          <Select {...field} label="Estado *">
                            {statuses.map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                <Chip label={s.label} color={s.color as any} size="small" />
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.status && (
                            <FormHelperText>{errors.status.message}</FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  
                  <Grid size={{ xs: 12 }}>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          multiline
                          rows={3}
                          label="Notas / Descripción"
                          placeholder="Motivo de la donación, información adicional..."
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Comprobante de Pago */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Comprobante de Pago
                </Typography>
                
                <input
                  type="file"
                  id="receipt-upload"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.pdf"
                />
                
                {uploadedFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      <OfferingIcon sx={{ fontSize: 40, mr: 2, color: 'success.main' }} />
                      <Box flex={1}>
                        <Typography variant="subtitle2">{uploadedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Comprobante cargado
                        </Typography>
                      </Box>
                      <IconButton color="error" onClick={handleRemoveFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : paymentFile ? (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Box display="flex" alignItems="center">
                      {filePreview ? (
                        <Box
                          component="img"
                          src={filePreview}
                          sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, mr: 2 }}
                        />
                      ) : (
                        <OfferingIcon sx={{ fontSize: 40, mr: 2 }} />
                      )}
                      <Box flex={1}>
                        <Typography variant="subtitle2">{paymentFile.name}</Typography>
                        <Typography variant="caption">{(paymentFile.size / 1024).toFixed(1)} KB</Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleFileUpload(paymentFile)}
                        disabled={uploadingFile}
                        sx={{ mr: 1 }}
                      >
                        {uploadingFile ? <CircularProgress size={20} /> : 'Subir'}
                      </Button>
                      <IconButton size="small" color="error" onClick={handleRemoveFile}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    sx={{
                      border: '2px dashed #ccc',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                    }}
                    onClick={() => document.getElementById('receipt-upload')?.click()}
                  >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography>Haz clic para subir un comprobante</Typography>
                    <Typography variant="caption">Formatos: JPG, PNG, PDF (máx. 5MB)</Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Botones */}
          <Grid size={{ xs: 12 }}>
            <Box display="flex" justifyContent="flex-end" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/donations')} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} disabled={loading || success}>
                {loading ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Diálogo de búsqueda de padres */}
      <Dialog open={searchParentOpen} onClose={() => setSearchParentOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Buscar Padre/Tutor</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar por nombre español o ruso..."
            value={parentSearch}
            onChange={(e) => setParentSearch(e.target.value)}
            sx={{ my: 2 }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          />
          {filteredParents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No se encontraron padres
            </Typography>
          ) : (
            <List>
              {filteredParents.map((parent) => (
                <ListItem key={parent.id} component="button" onClick={() => handleSelectParent(parent)}>
                  <ListItemAvatar>
                    <Avatar><PersonIcon /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={parent.spanish_name || parent.russian_name}
                    secondary={parent.email || parent.phone}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchParentOpen(false)}>Cerrar</Button>
          <Button 
            variant="outlined" 
            startIcon={<AddIcon />}
            onClick={() => {
              setSearchParentOpen(false);
              navigate('/parents/new');
            }}
          >
            Crear nuevo padre
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OfferingForm;